import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useSubmitQuoteItemEdits } from '~/api';
import { toastGraphQLError } from '~/utils';
import type { QuoteResponseSubmission } from '~/pages/quote-response/useQuoteResponseDraft';

/**
 * Saves the seller's answer to a quote, whether the seller or the buyer for them enters
 * it, and toasts the outcome. `save` resolves to whether it was saved.
 *
 * @example
 *   const { save, isSaving } = useSaveQuoteResponse();
 *   if (await save(quote.id, usesTallyPrice, submission)) close();
 */
export function useSaveQuoteResponse() {
  const { t } = useTranslation('rfq-view');
  const { mutateAsync: submitEdits, isPending: isSaving } =
    useSubmitQuoteItemEdits();

  const save = async (
    quoteId: string,
    usesTallyPrice: boolean,
    submission: QuoteResponseSubmission
  ): Promise<boolean> => {
    try {
      await submitEdits({
        quoteId,
        input: {
          itemOverrides: submission.itemOverrides,
          tallyPrice: {
            useTallyPrice: usesTallyPrice,
            offeredPickupPriceForTally: submission.wholeRequestPickupPrice,
            offeredDeliveryPriceForTally: submission.wholeRequestDeliveryPrice,
            currency: submission.currency,
          },
          sellingOrganizationFacilityId:
            submission.sellingOrganizationFacilityId,
          leadTimeDays: submission.leadTimeDays,
          submit: submission.submit,
        },
      });
      toast.success(
        submission.submit
          ? t('rfq_view.edits_submitted')
          : t('rfq_view.edits_saved')
      );
      return true;
    } catch (error) {
      toastGraphQLError(error);
      return false;
    }
  };

  return { save, isSaving };
}
